import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { Icon } from "@iconify/react";
import MasterLayout from "../../masterLayout/MasterLayout";
import Breadcrumb from "../../components/Breadcrumb";
import AffiliationModal from "../../components/store/AffiliationModal";
import { useAffiliate } from "../../hooks/useAffiliate";
import { selectUser } from "../../services/reducers/user";
import CurrencyFormatter from "../../utils/CurrencyFormatter";
import { getProductPriceDisplay } from '../../utils/productPricing';
import { storeService } from "../../services/api/store/storeApi";

const ProductDetailsPage = () => {
  const { id: productUuid } = useParams();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const { 
    affiliateStatus, 
    promoting, 
    promoteProduct 
  } = useAffiliate(productUuid);
  
  const [product, setProduct] = useState(null);
  const [otherProducts, setOtherProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPromoteMessage, setShowPromoteMessage] = useState(false);
  const [showAffiliationModal, setShowAffiliationModal] = useState(false);
  const [affiliationResult, setAffiliationResult] = useState(null);

  // Função helper para obter ID do usuário de múltiplas fontes
  const getUserId = () => {
    // 1. Tentar do Redux store
    if (user?.id) return user.id;
    
    // 2. Tentar do localStorage como backup
    try {
      const userData = JSON.parse(localStorage.getItem('user_data'));
      if (userData?.id) return userData.id;
    } catch (e) {
      // Ignorar erro
    }
    
    // 3. Tentar decodificar do token JWT
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      if (token) {
        const parts = token.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
          if (payload?.id || payload?.userId) return payload.id || payload.userId;
        }
      }
    } catch (e) {
      // Ignorar erro
    }
    
    return null;
  };

  // Função para buscar produto diretamente do backend
  const fetchProduct = async () => {
    try {
      setLoading(true);
      const data = await storeService.getProductDetails(productUuid);
      
      if (data.success) {
        setProduct(data.product);
        
        // Debug: verificar dados recebidos
        const currentUserId = getUserId();
        console.log('🔍 Produto recebido:', {
          product_user_id: data.product.user_id,
          user_id: currentUserId,
          isMyProduct: data.product.user_id && currentUserId && (Number(data.product.user_id) === Number(currentUserId))
        });
        
        // Buscar outros produtos do mesmo produtor
        if (data.product.producer_name) {
          const otherProductsData = await storeService.getProducts({ producer: data.product.producer_name, limit: 10 });
          
          if (otherProductsData.success) {
            // Filtrar o produto atual usando UUID
            const filtered = otherProductsData.products.filter(p => p.uuid !== productUuid);
            setOtherProducts(filtered.slice(0, 3));
          }
        }
      } else {
        console.error('Erro ao buscar produto:', data.message);
        // Produto não encontrado - vamos mostrar empty state
        setProduct(null);
      }
    } catch (error) {
      console.error('Erro ao buscar produto:', error);
      setProduct(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProduct();
  }, [productUuid]);

  if (loading) {
    return (
      <MasterLayout>
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: "400px" }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Carregando...</span>
            </div>
            <p className="text-secondary-light">Carregando detalhes do produto...</p>
          </div>
        </div>
      </MasterLayout>
    );
  }

  if (!product) {
    return (
      <MasterLayout>
        <div className="container-fluid py-5">
          <div className="row justify-content-center">
            <div className="col-lg-6 col-xl-5">
              <div className="text-center py-5">
                <Icon 
                  icon="solar:box-search-linear" 
                  width="120" 
                  className="text-muted mb-4" 
                />
                
                <h3 className="text-dark mb-3 fw-bold">
                  Produto não encontrado
                </h3>
                
                <p className="text-muted mb-4 fs-5" style={{ lineHeight: '1.6' }}>
                  O produto que você está procurando não existe ou não está mais disponível na loja.
                </p>

                <div className="d-flex flex-column flex-sm-row justify-content-center gap-3">
                  <button 
                    onClick={() => navigate('/store/store-products')}
                    className="btn btn-primary-600 text-white fw-medium px-4 py-3"
                  >
                    <Icon icon="solar:shop-bold" className="me-2" />
                    Voltar à Loja
                  </button>
                  
                  <button 
                    onClick={() => navigate('/Dashboard')}
                    className="btn btn-outline-secondary fw-medium px-4 py-3"
                  >
                    <Icon icon="solar:home-linear" className="me-2" />
                    Ir ao Dashboard
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </MasterLayout>
    );
  }

  const ProgressWithCircle = ({ score }) => {
    const percent = Math.min((score / 150) * 100, 100);

    return (
      <div className="position-relative w-100 mt-3" style={{ height: "30px" }}>
        <div
          className="progress h-8-px w-100 bg-neutral-200 radius-8 overflow-hidden"
          style={{
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
          }}
        >
          <div
            className="rounded-pill"
            style={{
              width: `${percent}%`,
              height: "8px",
              background: "#44ADD4",
            }}
          />
        </div>
        <div
          className="position-absolute"
          style={{
            left: `${percent}%`,
            top: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <div
            className="d-flex flex-column justify-content-center align-items-center"
            style={{
              width: "38px",
              height: "38px",
              borderRadius: "50%",
              background: "#44ADD4",
              color: "white",
              fontSize: "11px",
              fontWeight: "600",
              boxShadow: "0 0 4px rgba(0,0,0,0.1)",
              padding: "2px",
            }}
          >
            <div style={{ fontSize: "11px", lineHeight: "12px" }}>{score}</div>
            <div style={{ fontSize: "8px", opacity: 0.8, lineHeight: "10px" }}>
              Pag's
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <MasterLayout>
      <Breadcrumb 
        title={product.name}
        items={[
          { label: 'Home', link: '/Dashboard' },
          { label: 'Loja', link: '/store/store-products' },
          { label: product.name, link: '#' }
        ]}
      />
      
      <div className="row gy-4" style={{ marginTop: '24px' }}>
        {/* Coluna Principal - Detalhes do Produto */}
        <div className="col-lg-8">
          {/* Imagem e Informações Básicas */}
          <div className="card radius-12 mb-24">
            <div className="card-body p-24">
              <div className="row gy-4">
                <div className="col-lg-6">
                  {/* Imagem Principal */}
                  <div className="radius-12 overflow-hidden bg-neutral-50 d-flex align-items-center justify-content-center" 
                       style={{ height: "320px" }}>
                    <img
                      src={product.image || "https://cdn.pixabay.com/photo/2021/08/03/06/47/clock-6518632_1280.jpg"}
                      alt={product.name}
                      className="w-100 h-100 object-fit-cover"
                      style={{ filter: 'brightness(0.95)' }}
                      onError={(e) => {
                        e.target.src = "https://cdn.pixabay.com/photo/2021/08/03/06/47/clock-6518632_1280.jpg";
                      }}
                    />
                  </div>
                </div>
                
                <div className="col-lg-6">
                  {/* Informações do Produto */}
                  <div className="h-100 d-flex flex-column">
                    {/* Tags e Badges */}
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      <span className="badge bg-primary-600 text-white px-12 py-6 text-sm fw-medium radius-6">
                        {product.category || 'Produto Digital'}
                      </span>
                      {product.has_after_pay && (
                        <span className="badge bg-success-600 text-white px-12 py-6 text-sm fw-medium radius-6">
                          <Icon icon="solar:dollar-circle-bold" className="me-1" />
                          After Pay
                        </span>
                      )}
                    </div>

                    {/* Nome do Produto */}
                    <h1 className="text-2xl fw-bold text-neutral-900 mb-3 line-height-1-2">
                      {product.name}
                    </h1>
                    
                    {/* Código do Produto - Badge abaixo do nome */}
                    {product.product_code && (
                      <div className="mb-4">
                        <span className="badge text-primary px-2 py-1" style={{
                          backgroundColor: 'rgba(var(--bs-primary-rgb), 0.1)',
                          border: '1px solid rgba(var(--bs-primary-rgb), 0.2)',
                          borderRadius: '8px',
                          fontWeight: '500',
                          fontSize: '0.75rem'
                        }}>
                          {product.product_code}
                        </span>
                      </div>
                    )}

                    {/* Preço */}
                    <div className="bg-neutral-50 radius-12 p-20 mb-4">
                      <div className="text-center">
                        {(() => {
                          const priceDisplay = getProductPriceDisplay(product);
                          return (
                            <>
                              <div className="text-neutral-600 text-sm mb-1">
                                {priceDisplay.label ? priceDisplay.label : 'Preço do Produto'}
                              </div>
                              <div className="text-primary-600 text-3xl fw-bold">
                                {priceDisplay.price}
                              </div>
                            </>
                          );
                        })()}
                        <div className="text-neutral-500 text-sm">
                          À vista no PIX ou até 12x no cartão
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Performance Pag's */}
          <div className="card radius-12 mb-24">
            <div className="card-body p-24">
              <h6 className="text-lg mb-3">Performance Pag's</h6>
              <ProgressWithCircle score={product.score} />
              <p className="text-sm text-secondary-light mt-3 mb-0">
                Este produto tem uma performance de {product.score} pontos, indicando {
                  product.score >= 100 ? 'excelente' :
                  product.score >= 70 ? 'boa' :
                  product.score >= 40 ? 'regular' : 'baixa'
                } popularidade e engajamento no mercado.
              </p>
            </div>
          </div>

          {/* Descrição do Produto */}
          <div className="card radius-12 mb-24">
            <div className="card-body p-24">
              <h6 className="text-lg mb-3">Descrição do Produto</h6>
              <div 
                className="text-secondary-light mb-0"
                dangerouslySetInnerHTML={{ 
                  __html: product.description || 'Produto digital de alta qualidade desenvolvido por especialistas na área.'
                }}
              />
            </div>
          </div>

          {/* Planos Disponíveis */}
          <div className="card radius-12 mb-24">
            <div className="card-body p-24">
              <h6 className="text-lg mb-3">Planos Disponíveis</h6>
              <div className="row gy-3">
                {product.plans && product.plans.length > 0 ? (
                  product.plans.map((plan, index) => (
                    <div key={index} className="col-md-6">
                      <div className="border border-neutral-200 radius-12 p-16 h-100">
                        <h6 className="text-md fw-semibold mb-2">{plan.name}</h6>
                        <div className="text-primary-600 text-xl fw-bold mb-2">
                          {CurrencyFormatter.centsToFormatted(plan.price || 0)}
                        </div>
                        <p className="text-sm text-secondary-light mb-0">
                          {plan.description}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-12">
                    <div className="border border-neutral-200 radius-12 p-16">
                      <h6 className="text-md fw-semibold mb-2">Plano Único</h6>
                      <div className="text-primary-600 text-xl fw-bold mb-2">
                        {CurrencyFormatter.centsToFormatted(product.price || 0)}
                      </div>
                      <p className="text-sm text-secondary-light mb-0">
                        Pagamento único com acesso completo ao produto
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Outros Produtos do Mesmo Produtor */}
          <div className="card radius-12" style={{ overflow: 'hidden' }}>
            <div className="card-body p-24">
              <div 
              className="text-muted"
                style={{ 
                  fontSize: '18px',  
                  lineHeight: '1.5',
                  fontWeight: '600',
                  marginBottom: '24px',
                  padding: '0',
                  margin: '0 0 24px 0',
                  display: 'block',
                  position: 'relative',
                  zIndex: 10,
                  textShadow: 'none',
                  letterSpacing: 'normal',
                  textTransform: 'none'
                }}
              >
                Outros Produtos do Mesmo Produtor
              </div>
              <div className="row gy-4">
                {otherProducts.map((otherProduct, index) => (
                  <div key={index} className="col-md-4">
                    <Link
                      to={`/store/product/${otherProduct.uuid}`}
                      className="text-decoration-none"
                    >
                      <div className="card h-100 border-neutral-200 hover-scale-105 transition-all">
                        {/* Imagem */}
                        <div
                          className="position-relative overflow-hidden radius-top-12"
                          style={{ height: '160px' }}
                        >
                          <img
                            src={otherProduct.image || "https://cdn.pixabay.com/photo/2021/08/03/06/47/clock-6518632_1280.jpg"}
                            alt={otherProduct.name}
                            className="w-100 h-100 object-fit-cover"
                            style={{ filter: 'brightness(0.9)' }}
                            onError={(e) => {
                              e.target.src = "https://cdn.pixabay.com/photo/2021/08/03/06/47/clock-6518632_1280.jpg";
                            }}
                          />
                          
                          {/* Badge de Categoria */}
                          <div className="position-absolute top-12 start-12 z-3">
                            <span className="badge bg-primary-600 text-white px-10 py-6 text-xs fw-medium radius-6 shadow-sm">
                              {otherProduct.category || 'Geral'}
                            </span>
                          </div>
                        </div>
                        
                        {/* Conteúdo do Card */}
                        <div className="p-16">
                          {/* Nome do Produto */}
                          <div className="mb-12">
                            <span 
                              className="d-block mb-6 fw-semibold" 
                              style={{ fontSize: '16px', color: '#1f2937', lineHeight: '1.3' }}
                              title={otherProduct.name}
                            >
                              {otherProduct.name.length > 30 ? `${otherProduct.name.substring(0, 30)}...` : otherProduct.name}
                            </span>
                            
                            {/* Código do Produto */}
                            <div className="d-flex align-items-center gap-2 mb-8">
                              <Icon icon="solar:hashtag-linear" className="text-xs text-secondary-light" />
                              <span className="text-xs text-secondary-light fw-medium font-monospace">
                                {otherProduct.code}
                              </span>
                            </div>
                          </div>

                          {/* Comissão */}
                          <div className="mb-16">
                            <div className="text-center bg-neutral-50 radius-8 py-12">
                              <div className="text-xs text-secondary-light mt-1">
                                Comissão: {otherProduct.commission_percentage}%
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
              {otherProducts.length === 0 && (
                <p className="text-secondary-light text-sm mb-0">Este produtor ainda não possui outros produtos na loja.</p>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar - Ações e Informações */}
        <div className="col-lg-4">
          {/* Card de Afiliação */}
          <div className="card radius-12 mb-24">
            <div className="card-body p-24">
              {/* Informações de Comissão */}
              <div className="bg-success-50 radius-8 p-16 mb-20">
                <div className="text-center">
                  <h6 className="text-success-600 mb-1">Sua Comissão</h6>
                  <div className="text-success-600 text-xl fw-bold">
                    {product.commission_percentage}%
                  </div>
                  <p className="text-success-600 text-sm mb-0">
                    Por cada venda realizada
                  </p>
                </div>
              </div>

              {/* Botões de Ação */}
              <div className="d-grid gap-3">
                {/* Verificar se o produto é do próprio usuário (apenas se usuário estiver logado) */}
                {(() => {
                  const currentUserId = getUserId();
                  const isMyProduct = currentUserId && product.user_id && (Number(product.user_id) === Number(currentUserId));
                  console.log('🔍 Verificação de propriedade:', {
                    currentUserId,
                    product_user_id: product.user_id,
                    isMyProduct,
                    userType: typeof currentUserId,
                    productType: typeof product.user_id
                  });
                  return isMyProduct;
                })() ? (
                  <div 
                    className="btn fw-medium py-12 radius-8 position-relative d-flex align-items-center justify-content-center"
                    style={{ 
                      cursor: 'not-allowed', 
                      backgroundColor: '#e5e7eb',
                      color: '#6b7280',
                      border: '1px solid #d1d5db'
                    }}
                    title="Você não pode promover seu próprio produto"
                  >
                    <Icon icon="solar:forbidden-circle-bold" className="me-2" />
                    Este é o seu produto
                  </div>
                ) : affiliateStatus.isAffiliate ? (
                  <>
                    <div 
                      className={`btn fw-medium py-12 radius-8 position-relative d-flex align-items-center justify-content-center ${
                        affiliateStatus.status === 'approved' 
                          ? 'btn-success-600 text-white' 
                          : affiliateStatus.status === 'pending'
                          ? 'btn-warning-600 text-white'
                          : 'btn-secondary text-white'
                      }`}
                      style={{ cursor: 'default' }}
                    >
                      <Icon 
                        icon={
                          affiliateStatus.status === 'approved' 
                            ? "solar:check-circle-bold" 
                            : affiliateStatus.status === 'pending'
                            ? "solar:clock-circle-bold"
                            : "solar:minus-circle-bold"
                        } 
                        className="me-2" 
                      />
                      {affiliateStatus.status === 'approved' 
                        ? 'Você já promove este produto' 
                        : affiliateStatus.status === 'pending'
                        ? 'Aguardando aprovação'
                        : 'Afiliação rejeitada'
                      }
                    </div>
                    
                    {/* Botão para ir à área do afiliado (só para aprovados) */}
                    {affiliateStatus.status === 'approved' && (
                      <button 
                        onClick={() => navigate(`/affiliate/product/${product?.id}`)}
                        className="btn btn-outline-success-600 text-success-600 fw-medium py-12 radius-8"
                      >
                        <Icon icon="solar:shield-star-bold" className="me-2" />
                        Ir para Área do Afiliado
                      </button>
                    )}
                  </>
                ) : (
                  <button 
                    onClick={async () => {
                      const result = await promoteProduct();
                      setAffiliationResult(result);
                      setShowAffiliationModal(true);
                      
                      // Se foi aprovado automaticamente, redirecionar para área do afiliado
                      if (result?.success && result?.autoApproved) {
                        setTimeout(() => {
                          navigate(`/affiliate/product/${product?.id}`);
                        }, 2000);
                      }
                      
                      // Remove a mensagem antiga se existir
                      if (showPromoteMessage) {
                        setShowPromoteMessage(false);
                      }
                    }}
                    disabled={promoting}
                    className="btn btn-success-600 text-white fw-medium py-12 radius-8 position-relative d-flex align-items-center justify-content-center"
                  >
                    {promoting ? (
                      <>
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                          <span className="visually-hidden">Carregando...</span>
                        </div>
                        Processando...
                      </>
                    ) : (
                      <>
                        <Icon icon="solar:megaphone-bold" className="me-2" />
                        Promover este Produto
                      </>
                    )}

                  </button>
                )}

                {showPromoteMessage && (
                  <div className="alert alert-success alert-dismissible fade show radius-8" role="alert">
                    <Icon icon="solar:check-circle-bold" className="me-2" />
                    {showPromoteMessage}
                    <button 
                      type="button" 
                      className="btn-close" 
                      onClick={() => setShowPromoteMessage(false)}
                    ></button>
                  </div>
                )}
                
                {product.sales_page && (
                  <a 
                    href={product.sales_page} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary-600 text-primary-600 fw-medium py-12 radius-8 text-decoration-none d-flex align-items-center justify-content-center gap-2"
                  >
                    <Icon icon="solar:link-round-angle-linear" />
                    Ver Página de Vendas
                    <Icon icon="solar:external-link-linear" className="text-xs" />
                  </a>
                )}
              </div>

              <div className="mt-20 pt-20 border-top">
                <div className="d-flex align-items-center justify-content-between text-sm mb-2">
                  <span className="text-secondary-light">Tipo de Comissão</span>
                  <span className="fw-medium">
                    {product.commission_type === 'percentage' ? 'Percentual' : 'Fixo'}
                  </span>
                </div>
                <div className="d-flex align-items-center justify-content-between text-sm mb-2">
                  <span className="text-secondary-light">Garantia</span>
                  <span className="fw-medium">30 dias</span>
                </div>
                <div className="d-flex align-items-center justify-content-between text-sm">
                  <span className="text-secondary-light">Categoria</span>
                  <span className="fw-medium">{product.category}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Card do Produtor */}
          <div className="card radius-12">
            <div className="card-body p-24">
              <h6 className="text-lg mb-3">Sobre o Produtor</h6>
              <div className="d-flex align-items-center gap-3">
                <div 
                  className="bg-primary-100 rounded-circle d-flex align-items-center justify-content-center"
                  style={{ width: "50px", height: "50px" }}
                >
                  <Icon icon="solar:shop-2-bold" className="text-xl text-primary-600" />
                </div>
                <div>
                  <h6 className="text-md mb-0">{product.producer_name || product.producer}</h6>
                  <span className="text-sm text-secondary-light">Produtor</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Botão de Voltar */}
      <div className="mt-24">
        <button
          onClick={() => navigate('/store/store-products')}
          className="btn btn-primary-600 text-white fw-medium px-20 py-10 radius-8"
        >
          <Icon icon="solar:arrow-left-linear" className="me-2" />
          Voltar para a Loja
        </button>
      </div>

      {/* Affiliation Modal */}
      <AffiliationModal 
        show={showAffiliationModal}
        onHide={() => setShowAffiliationModal(false)}
        productName={product?.name}
        isSuccess={affiliationResult?.success}
        message={affiliationResult?.message}
        autoApproved={affiliationResult?.auto_approved}
      />
    </MasterLayout>
  );
};

export default ProductDetailsPage;